import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import Dropdown from "../../common/dropDown";
import DateRangeFilter from "../../common/DateRangeFilter";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import BulkCommunicatePanel from "./BulkCommunicatePanel";
import {
  fetchBulkCommGroupsAndVisits,
  fetchPlannerStudyList,
  fetchStudyWiseVolunteers,
} from "./participantService";
import {
  mapGroupOptions,
  mapStudyOptions,
  mapVisitOptions,
} from "../eplanner/eplannerReportUtils";
import {
  getVolunteerStatusPillStyle,
  PM_DOB_DATE_RANGE_INITIAL,
} from "./participantReportUtils";
import { useParticipantAccess } from "./participantAccess";
import "./participantmanager.scss";

const APPT_STATUS_OPTS = [
  { key: "arrived", label: "Arrived", value: "Arrived" },
  { key: "confirmed", label: "Confirmed", value: "Confirmed" },
  { key: "dna", label: "Did Not Attend", value: "NotAttempt" },
  { key: "cancelled", label: "Cancelled", value: "Cancelled" },
];

const VOL_STATUS_OPTS = [
  { key: "active", label: "Active", value: "0" },
  { key: "withdrawn", label: "Withdrawn", value: "1" },
  { key: "screenFailed", label: "Screen Failed", value: "2" },
];

const APPT_TYPE_OPTS = [
  { key: "clinic", label: "Clinic", value: "Clinic" },
  { key: "virtual", label: "Virtual", value: "Virtual" },
  { key: "telephonic", label: "Telephonic", value: "Telephone" },
];

const DEFAULT_APPT_STATUS = {
  arrived: true,
  confirmed: true,
  dna: true,
  cancelled: true,
};

const DEFAULT_VOL_STATUS = {
  active: true,
  withdrawn: true,
  screenFailed: true,
};

const DEFAULT_APPT_TYPE = {
  clinic: true,
  virtual: true,
  telephonic: true,
};

function pick(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
}

function toBoolFlag(value) {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    /^true|yes$/i.test(String(value ?? "").trim())
  );
}

function joinChecked(opts, selected) {
  return opts
    .filter((o) => selected[o.key])
    .map((o) => o.value)
    .join(",");
}

function toApiDate(iso) {
  const raw = String(iso || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [y, m, d] = raw.split("-");
  return `${m}/${d}/${y}`;
}

function toFilterPeriod(dateRange) {
  const start = toApiDate(dateRange?.startDate);
  const end = toApiDate(dateRange?.endDate);
  if (!start && !end) {
    return { StartPeriod: "01/01/2000", EndPeriod: "12/31/2099" };
  }
  return { StartPeriod: start, EndPeriod: end };
}

function mapVolunteerRows(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    IsEmail: toBoolFlag(row?.IsEmail ?? row?.isEmail),
    IsSMS: toBoolFlag(row?.IsSMS ?? row?.isSMS),
    EmailId: pick(row, "EmailId", "emailId", "Email"),
    ContactNumber: pick(row, "ContactNumber", "ContactNo", "Mobile"),
    VolunteerName: pick(row, "VolunteerName", "volunteerName", "Name"),
    VolunteerNo: pick(row, "VolunteerId", "VolunteerNo", "volunteerNo"),
    VolunteerId: pick(row, "VolIdRef", "VolunteerDemRecordId", "VolunteerId"),
    StudyNumber: pick(row, "StudyNumber", "StudyName", "studyNumber"),
    VisitName: pick(row, "VisitName", "visitName"),
    AppointmentDate: pick(
      row,
      "AppointmentDate",
      "AppointmentDateTime",
      "appointmentDate",
    ),
    VolunteerStatus: pick(row, "VolunteerStatus", "volunteerStatus"),
    AppointmentType: pick(row, "AppointmentType", "appointmentType"),
  }));
}

function ParticipantCard({ row }) {
  const statusLabel = row.VolunteerStatus || "—";
  const statusStyle = getVolunteerStatusPillStyle(row.VolunteerStatus);

  return (
    <article className="pm-bulk-person-card">
      <div className="pm-bulk-person-card__top">
        <div className="pm-bulk-person-card__title">
          {row.VolunteerNo ? (
            <span
              className="pm-status-pill pm-status-pill--readonly pm-status-pill--square pm-bulk-person-card__id-badge"
              title={row.VolunteerNo}
            >
              {row.VolunteerNo}
            </span>
          ) : null}
          <div
            className="pm-bulk-person-card__name"
            title={row.VolunteerName || ""}
          >
            {row.VolunteerName || "—"}
          </div>
        </div>
        <span
          className="pm-status-pill pm-status-pill--readonly pm-status-pill--square"
          style={statusStyle}
          title={statusLabel}
        >
          {statusLabel}
        </span>
      </div>

      <div className="pm-bulk-person-card__meta">
        <span title={row.StudyNumber || ""}>
          <i className="fas fa-flask" aria-hidden="true" />
          {row.StudyNumber || "—"}
        </span>
        <span title={row.VisitName || ""}>
          <i className="fas fa-calendar-check" aria-hidden="true" />
          {row.VisitName || "—"}
        </span>
        <span title={row.AppointmentDate || ""}>
          <i className="fas fa-clock" aria-hidden="true" />
          {row.AppointmentDate || "—"}
        </span>
        <span title={row.AppointmentType || ""}>
          <i className="fas fa-stethoscope" aria-hidden="true" />
          {row.AppointmentType || "—"}
        </span>
      </div>

      <div className="pm-bulk-person-card__footer">
        <span
          className="pm-status-pill pm-status-pill--readonly pm-status-pill--square pm-bulk-person-card__badge--email"
          title={row.EmailId || "No email"}
        >
          <i className="fas fa-envelope" aria-hidden="true" />
          <span className="pm-bulk-person-card__badge-text">
            {row.EmailId || "—"}
          </span>
        </span>
        <span
          className="pm-status-pill pm-status-pill--readonly pm-status-pill--square pm-bulk-person-card__badge--phone"
          title={row.ContactNumber || "No contact"}
        >
          <i className="fas fa-phone" aria-hidden="true" />
          <span className="pm-bulk-person-card__badge-text">
            {row.ContactNumber || "—"}
          </span>
        </span>
      </div>
    </article>
  );
}

const BulkEmailSms = () => {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const { canSendBulkComm, loading: accessLoading } = useParticipantAccess();

  const [studyRows, setStudyRows] = useState([]);
  const [groupRows, setGroupRows] = useState([]);
  const [visitRows, setVisitRows] = useState([]);
  const [selectedStudyId, setSelectedStudyId] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedVisitIds, setSelectedVisitIds] = useState([]);
  const [dateRange, setDateRange] = useState(PM_DOB_DATE_RANGE_INITIAL);
  const [apptStatus, setApptStatus] = useState({ ...DEFAULT_APPT_STATUS });
  const [volStatus, setVolStatus] = useState({ ...DEFAULT_VOL_STATUS });
  const [apptType, setApptType] = useState({ ...DEFAULT_APPT_TYPE });

  const [resultRows, setResultRows] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const filterSeqRef = useRef(0);

  const studyOptions = useMemo(() => mapStudyOptions(studyRows), [studyRows]);

  const groupOptions = useMemo(
    () =>
      mapGroupOptions(
        groupRows,
        selectedStudyId ? [selectedStudyId] : [],
        studyOptions,
      ),
    [groupRows, selectedStudyId, studyOptions],
  );

  const visitOptions = useMemo(() => {
    const opts = mapVisitOptions(
      visitRows,
      selectedGroupId ? [selectedGroupId] : [],
      { studyOptions, groupOptions },
    );
    if (opts.length > 1) {
      return [...opts, { value: "__ADHOC__", label: "Adhoc Visit" }];
    }
    return opts;
  }, [visitRows, selectedGroupId, studyOptions, groupOptions]);

  const selectedVisitOptions = useMemo(
    () =>
      visitOptions.filter((opt) =>
        selectedVisitIds.some(
          (id) => String(id).toUpperCase() === String(opt.value).toUpperCase(),
        ),
      ),
    [visitOptions, selectedVisitIds],
  );

  const loadStudies = useCallback(async () => {
    if (!userData?.OrganizationId) return;
    showLoader("Loading studies...");
    try {
      const list = await fetchPlannerStudyList(userData);
      setStudyRows(Array.isArray(list) ? list : []);
    } catch (err) {
      showAlert({ message: err?.message || "Could not load study list." });
      setStudyRows([]);
    } finally {
      hideLoader();
    }
  }, [hideLoader, showAlert, showLoader, userData]);

  useEffect(() => {
    loadStudies();
  }, [loadStudies]);

  const runFilter = useCallback(async () => {
    if (
      !userData?.OrganizationId ||
      !selectedStudyId ||
      !selectedGroupId ||
      !selectedVisitIds.length
    ) {
      setResultRows([]);
      setIsFiltering(false);
      return;
    }

    const seq = ++filterSeqRef.current;
    setIsFiltering(true);

    const visitIdsForApi = selectedVisitIds.map((id) =>
      id === "__ADHOC__" ? "" : id,
    );

    try {
      const rows = await fetchStudyWiseVolunteers(userData, {
        StudyId: selectedStudyId,
        VisitIds: visitIdsForApi,
        AppointmentStatus: joinChecked(APPT_STATUS_OPTS, apptStatus),
        VolunteerStatus: joinChecked(VOL_STATUS_OPTS, volStatus),
        AppointmentType: joinChecked(APPT_TYPE_OPTS, apptType),
        ActionType: "",
        ...toFilterPeriod(dateRange),
      });
      if (seq !== filterSeqRef.current) return;
      setResultRows(mapVolunteerRows(rows));
    } catch (err) {
      if (seq !== filterSeqRef.current) return;
      setResultRows([]);
      showAlert({
        message: err?.message || "Could not load filtered participants.",
      });
    } finally {
      if (seq === filterSeqRef.current) setIsFiltering(false);
    }
  }, [
    apptStatus,
    apptType,
    dateRange,
    selectedGroupId,
    selectedStudyId,
    selectedVisitIds,
    showAlert,
    userData,
    volStatus,
  ]);

  useEffect(() => {
    if (!selectedStudyId || !selectedGroupId || !selectedVisitIds.length) {
      setResultRows([]);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      runFilter();
    }, 350);
    return () => window.clearTimeout(timer);
  }, [
    selectedStudyId,
    selectedGroupId,
    selectedVisitIds,
    dateRange,
    apptStatus,
    volStatus,
    apptType,
    runFilter,
  ]);

  const handleStudySelect = async (opt) => {
    const id = String(opt?.value || "").trim();
    setSelectedStudyId(id);
    setSelectedGroupId("");
    setSelectedVisitIds([]);
    setGroupRows([]);
    setVisitRows([]);
    setResultRows([]);
    if (!id || !userData?.OrganizationId) return;

    showLoader("Loading groups & visits...");
    try {
      const { groups, visits } = await fetchBulkCommGroupsAndVisits(
        userData,
        id,
      );
      const nextGroups = Array.isArray(groups) ? groups : [];
      const nextVisits = Array.isArray(visits) ? visits : [];
      setGroupRows(nextGroups);
      setVisitRows(nextVisits);

      const nextGroupOpts = mapGroupOptions(
        nextGroups,
        id ? [id] : [],
        studyOptions,
      );
      if (nextGroupOpts.length === 1) {
        const groupId = String(nextGroupOpts[0].value || "").trim();
        setSelectedGroupId(groupId);
        const nextVisitOpts = mapVisitOptions(nextVisits, [groupId], {
          studyOptions,
          groupOptions: nextGroupOpts,
        });
        const visitIds = nextVisitOpts.map((o) => o.value).filter(Boolean);
        setSelectedVisitIds(
          visitIds.length > 1 ? [...visitIds, "__ADHOC__"] : visitIds,
        );
      }

      if (!nextGroups.length) {
        showAlert({
          message: "No groups found for this study.",
          type: "warning",
        });
      }
    } catch (err) {
      showAlert({
        message: err?.message || "Could not load groups and visits.",
      });
      setGroupRows([]);
      setVisitRows([]);
    } finally {
      hideLoader();
    }
  };

  const handleGroupSelect = (opt) => {
    const id = String(opt?.value || "").trim();
    setSelectedGroupId(id);
    setResultRows([]);
    const nextVisitOpts = mapVisitOptions(visitRows, id ? [id] : [], {
      studyOptions,
      groupOptions,
    });
    const ids = nextVisitOpts.map((o) => o.value).filter(Boolean);
    setSelectedVisitIds(ids.length > 1 ? [...ids, "__ADHOC__"] : ids);
  };

  const handleVisitSelect = (opts) => {
    const list = Array.isArray(opts) ? opts : opts ? [opts] : [];
    setSelectedVisitIds(list.map((o) => String(o?.value ?? "")));
  };

  const clearFilters = () => {
    filterSeqRef.current += 1;
    setSelectedStudyId("");
    setSelectedGroupId("");
    setSelectedVisitIds([]);
    setGroupRows([]);
    setVisitRows([]);
    setDateRange({ ...PM_DOB_DATE_RANGE_INITIAL });
    setApptStatus({ ...DEFAULT_APPT_STATUS });
    setVolStatus({ ...DEFAULT_VOL_STATUS });
    setApptType({ ...DEFAULT_APPT_TYPE });
    setResultRows([]);
    setIsFiltering(false);
  };

  const hasActiveFilters = Boolean(
    selectedStudyId ||
      selectedGroupId ||
      selectedVisitIds.length ||
      (dateRange?.preset && dateRange.preset !== "All Time") ||
      dateRange?.startDate ||
      dateRange?.endDate ||
      JSON.stringify(apptStatus) !== JSON.stringify(DEFAULT_APPT_STATUS) ||
      JSON.stringify(volStatus) !== JSON.stringify(DEFAULT_VOL_STATUS) ||
      JSON.stringify(apptType) !== JSON.stringify(DEFAULT_APPT_TYPE),
  );

  const nextBlockedReason = !selectedStudyId
    ? "Select a study, group and visit to load participants."
    : !selectedGroupId
      ? "Select a group and visit to load participants."
      : !selectedVisitIds.length
        ? "Select at least one visit to load participants."
        : isFiltering
          ? "Loading participants. Please wait."
          : !resultRows.length
            ? "No participants match the current filters. Change or clear filters, then try again."
            : "";

  if (accessLoading) {
    return <div className="participant-manager-page pm-page--bootstrapping" />;
  }
  if (!canSendBulkComm) {
    return <Navigate to="/notification-center" replace />;
  }

  const renderCheckList = (opts, state, setState) => (
    <div className="pm-bulk-check-list">
      {opts.map((o) => (
        <label key={o.key} className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            checked={Boolean(state[o.key])}
            onChange={(e) =>
              setState((prev) => ({ ...prev, [o.key]: e.target.checked }))
            }
          />
          <span className="form-check-label">{o.label}</span>
        </label>
      ))}
    </div>
  );

  const emptyHint = !selectedStudyId
    ? "Select a study to load participants."
    : !selectedGroupId
      ? "Select a group to continue."
      : !selectedVisitIds.length
        ? "Select at least one visit."
        : isFiltering
          ? "Loading participants…"
          : "No participants found for the selected filters.";

  return (
    <div className="participant-manager-page pm-list-page pm-bulk-page">
      <PageBreadcrumb
        items={[
          { label: "Participant Manager" },
          { label: "Send Bulk Email/SMS" },
        ]}
      />

      <div className="pm-bulk-columns">
        {/* 1 — Filter */}
        <aside className="pm-bulk-columns__filters" aria-label="Filter">
          <div className="pm-bulk-filter-panel">
            <div className="pm-bulk-filter-panel__head">
              <div className="pm-bulk-filter-panel__title">Filter</div>
              {hasActiveFilters ? (
                <button
                  type="button"
                  className="btn btn-link btn-sm px-0 pm-bulk-filter-panel__clear"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              ) : null}
            </div>

            <div className="pm-bulk-filter-field">
              <label className="form-label small mb-1">
                Study <span className="pm-add-field__req">*</span>
              </label>
              <Dropdown
                options={studyOptions}
                selected={
                  studyOptions.find((o) => o.value === selectedStudyId) || null
                }
                onSelect={handleStudySelect}
              />
            </div>

            <div className="pm-bulk-filter-field">
              <label className="form-label small mb-1">
                Group <span className="pm-add-field__req">*</span>
              </label>
              <Dropdown
                options={groupOptions}
                selected={
                  groupOptions.find((o) => o.value === selectedGroupId) || null
                }
                onSelect={handleGroupSelect}
              />
            </div>

            <div className="pm-bulk-filter-field">
              <label className="form-label small mb-1">
                Visit <span className="pm-add-field__req">*</span>
              </label>
              <Dropdown
                isMulti
                options={visitOptions}
                selected={selectedVisitOptions}
                onSelect={handleVisitSelect}
              />
              {!visitOptions.length ? (
                <div className="text-muted small mt-1">
                  {selectedGroupId
                    ? "No visits for this group."
                    : "Select study & group first."}
                </div>
              ) : null}
            </div>

            <div className="pm-bulk-filter-field">
              <label className="form-label small mb-1">Date range</label>
              <DateRangeFilter
                value={dateRange}
                onChange={setDateRange}
                placeholder="Date range"
                compact
              />
            </div>

            <div className="pm-bulk-filter-field">
              <span className="pm-bulk-filter-field__group-label">
                Appointment Status
              </span>
              {renderCheckList(APPT_STATUS_OPTS, apptStatus, setApptStatus)}
            </div>
            <div className="pm-bulk-filter-field">
              <span className="pm-bulk-filter-field__group-label">
                Participant Status
              </span>
              {renderCheckList(VOL_STATUS_OPTS, volStatus, setVolStatus)}
            </div>
            <div className="pm-bulk-filter-field">
              <span className="pm-bulk-filter-field__group-label">
                Appointment Type
              </span>
              {renderCheckList(APPT_TYPE_OPTS, apptType, setApptType)}
            </div>
          </div>
        </aside>

        {/* 2 — Participants (cards) */}
        <section
          className="pm-bulk-columns__participants"
          aria-label="Participants"
        >
          <div className="pm-bulk-participants">
            <div className="pm-bulk-participants__head">
              <h2 className="h6 mb-0">Participants</h2>
              <span className="text-muted small">
                {isFiltering
                  ? "Updating…"
                  : resultRows.length
                    ? `${resultRows.length} recipient(s)`
                    : "No recipients yet"}
              </span>
            </div>

            <div
              className={`pm-bulk-participants__list${
                isFiltering ? " is-loading" : ""
              }`}
            >
              {resultRows.length ? (
                resultRows.map((row, index) => (
                  <ParticipantCard
                    key={`${row.VolunteerId || row.VolunteerNo || "row"}-${index}`}
                    row={row}
                  />
                ))
              ) : (
                <div className="pm-bulk-empty">{emptyHint}</div>
              )}
            </div>
          </div>
        </section>

        {/* 3 — Send communication (inline) */}
        <aside
          className="pm-bulk-columns__send"
          aria-label="Send communication"
        >
          <BulkCommunicatePanel
            recipients={resultRows}
            blockedReason={nextBlockedReason}
          />
        </aside>
      </div>

      <InfoAlert {...alertState} onClose={hideAlert} />
    </div>
  );
};

export default BulkEmailSms;
